"use client";

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Wrench, Trees as Tree, ChevronLeft, ChevronRight } from 'lucide-react';

export const Portfolio = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);

  const projects = [
    {
      title: t('portfolio.kraft.title'),
      description: t('portfolio.kraft.description'),
      features: t('portfolio.kraft.features', { returnObjects: true }) as string[],
      icon: <Wrench className="w-8 h-8 text-blue-300" />,
      link: 'https://kraftandtools.com/',
      tags: ['B2B', 'E-commerce', 'Product Catalog']
    },
    {
      title: t('portfolio.iglak.title'),
      description: t('portfolio.iglak.description'),
      features: t('portfolio.iglak.features', { returnObjects: true }) as string[],
      icon: <Tree className="w-8 h-8 text-green-300" />,
      link: 'https://iglakmierzejewski.pl/',
      tags: ['E-commerce', 'Multilingual', 'Garden Center']
    }
  ];

  const nextProject = () => {
    setCurrentIndex((prev) => (prev + 1) % projects.length);
  };

  const prevProject = () => {
    setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length);
  };

  return (
    <section id="portfolio" className="py-20 bg-gradient-to-b from-slate-900/20 to-blue-600/20">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-12 text-gradient text-glow">{t('portfolio.title')}</h2>
        
        <div className="max-w-5xl mx-auto">
          {/* Desktop View */}
          <div className="hidden md:grid md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10 transition-all duration-300 hover:bg-white/10 hover:scale-105"
              >
                <ProjectCard project={project} t={t} />
              </div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden relative">
            <div className="overflow-hidden">
              <div
                className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10 transition-all duration-300"
              >
                <ProjectCard project={projects[currentIndex]} t={t} />
              </div>
            </div>

            {/* Navigation Controls */}
            <div className="flex justify-center items-center mt-6 space-x-4">
              <button
                onClick={prevProject}
                className="p-2 bg-blue-500/20 text-blue-300 rounded-full border border-blue-500/30 hover:bg-blue-500/30 transition-all"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              <div className="flex space-x-2">
                {projects.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex
                        ? 'bg-blue-300'
                        : 'bg-blue-500/20'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={nextProject}
                className="p-2 bg-blue-500/20 text-blue-300 rounded-full border border-blue-500/30 hover:bg-blue-500/30 transition-all"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

interface ProjectCardProps {
  project: {
    title: string;
    description: string;
    features: string[];
    icon: React.ReactNode;
    link: string;
    tags: string[];
  };
  t: (key: string) => string;
}

const ProjectCard = ({ project, t }: ProjectCardProps) => (
  <div className="p-8">
    <div className="mb-6">{project.icon}</div>
    <h3 className="text-xl font-bold mb-3 text-white">{project.title}</h3>
    <p className="text-blue-200/80 mb-6">{project.description}</p>
    
    <div className="space-y-2 mb-6">
      {Array.isArray(project.features) && project.features.map((feature: string, i: number) => (
        <div key={i} className="flex items-center text-blue-200/80 text-sm">
          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mr-2" />
          {feature}
        </div>
      ))}
    </div>

    <div className="flex flex-wrap gap-2 mb-6">
      {project.tags.map((tag: string, tagIndex: number) => (
        <span
          key={tagIndex}
          className="px-3 py-1 bg-blue-500/10 text-blue-300 text-sm rounded-full border border-blue-500/30 hover:bg-blue-500/20 transition-all"
        >
          {tag}
        </span>
      ))}
    </div>
    
    <div className="flex flex-col space-y-3">
      <a
        href={project.link}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center text-blue-300 hover:text-blue-200 transition-colors"
      >
        <span className="mr-2">{t('portfolio.visitSite')}</span>
        <ExternalLink className="w-4 h-4" />
      </a>
      <a
        href="#contact"
        className="inline-flex items-center text-blue-300 hover:text-blue-200 transition-colors"
      >
        <span className="mr-2">{t('portfolio.getSimilar')}</span>
        <ChevronRight className="w-4 h-4" />
      </a>
    </div>
  </div>
);
